import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, Typography } from '@mui/material';
import {
  pxToRem,
  toStringBloomLevel,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Question } from '@protos/content_management/content.db_pb';
import {
  IConceptSummary,
  IPerformanceQuestionData,
  IPerformanceSummary,
} from '../../api/learners';
import { AnswerModeEnum, Score } from '../../api/common';
import { IKnowledgeConcept } from '../../KnowledgeConcepts/knowledgeConcepts.types';
import {
  getUserSubmittedDisplayAnswer,
  getCorrectDisplayAnswer,
  getAllReviewOptions,
  resolveReviewResultType,
  buildOverlayCopy,
} from '../PalAnswerOverlay';
import {
  AllOptionsSection,
  CorrectAnswerSection,
  SolutionSection,
  UserAnswerSection,
} from '../AnswerReviewSections';
import {
  QuestionAnswerReviewBox,
  ReviewResultStatus,
} from '../QuestionAnswerReviewBox';
import { ReviewAnswerSolutionTabs } from '../ReviewAnswerSolutionTabs';
import { ExplanationHelpfulFeedback } from '../ExplanationHelpfulFeedback';
import { ReportIssueButton } from '../ReportIssueButton';
import { IModuleMasteryData } from '../../api/module';
import { TopbarCard } from '../EndSessionDrawer/components/TopbarCard';
import { ConceptSection } from '../EndSessionDrawer/components/ConceptSection';
import { StrengthWeaknessSection } from '../PerformanceDrawer/components/StrengthWeaknessSection';
import { BLOOM_CONFIG } from '../PerformanceDrawer/performanceDrawerData';
import { DifficultyBreakdownSection } from '../EndSessionDrawer/components/DifficultyBreakdownSection';

// ═══════════════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════════════

interface ConceptBreakdownItem {
  id: string;
  name: string;
  emoji: string;
  total: number;
  correct: number;
  bloomLevels: string[];
  level: 'strong' | 'weak' | 'not-assessed';
  status: string;
}

export interface ReviewResult {
  result: IPerformanceSummary;
  summaryText?: string;
  moduleMastery?: IModuleMasteryData;
  knowledgeConcept?: IKnowledgeConcept;
}

// ═══════════════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════════════

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

function getQuestionReviewStatus(
  question: IPerformanceQuestionData
): ReviewResultStatus {
  const mode = question.eventReqBody?.answer_mode;
  if (mode === AnswerModeEnum.SKIPPED) return 'skipped';
  if (mode === AnswerModeEnum.TIMEOUT) return 'timeout';

  const score = (question.eventReqBody?.score as Score)?.value as
    | number
    | undefined;
  const answers = question.eventReqBody?.answer;
  const hasAnswer =
    Array.isArray(answers) &&
    answers.some((a) => a != null && String(a).length > 0);

  if (
    mode !== AnswerModeEnum.ANSWERED &&
    Array.isArray(answers) &&
    !hasAnswer &&
    (score === 0 || score === undefined)
  ) {
    return 'skipped';
  }

  return resolveReviewResultType(score);
}

const QUESTION_CHIP_STYLES: Record<
  ReviewResultStatus,
  { borderColor: string; bgcolor: string; color: string }
> = {
  correct: { borderColor: '#bbf7d0', bgcolor: '#f0fdf4', color: '#16a34a' },
  incorrect: { borderColor: '#fecaca', bgcolor: '#fef2f2', color: '#dc2626' },
  partial: { borderColor: '#fde68a', bgcolor: '#fffbeb', color: '#d97706' },
  skipped: { borderColor: '#fde68a', bgcolor: '#fffbeb', color: '#d97706' },
  timeout: { borderColor: '#fde68a', bgcolor: '#fffbeb', color: '#d97706' },
};

function getScoreMessage(pct: number): string {
  if (pct >= 80) return 'PAL_PP_SCORE_EXCELLENT';
  if (pct >= 50) return 'PAL_PP_SCORE_GOOD';
  return 'PAL_PP_SCORE_NICE';
}

function buildConceptBreakdown(
  concepts: IConceptSummary[],
  questions: IPerformanceQuestionData[],
  moduleMastery: IModuleMasteryData | undefined,
  knowledgeConcept: IKnowledgeConcept | undefined,
  includeMissingConcepts?: boolean
): ConceptBreakdownItem[] {
  const conceptMap = new Map<string, IPerformanceQuestionData[]>();
  for (const q of questions) {
    if (!q.concept_id) continue;
    const list = conceptMap.get(q.concept_id) ?? [];
    list.push(q);
    conceptMap.set(q.concept_id, list);
  }

  const masteryMap = new Map(
    moduleMastery?.concept_states.map((c) => [
      c.concept_id,
      c.concept_status,
    ]) ?? []
  );

  const assessed: ConceptBreakdownItem[] = concepts.map((c) => {
    const isAssessed = c.total_questions > 0;
    const pct = isAssessed ? c.total_correct_questions / c.total_questions : 0;
    const level: ConceptBreakdownItem['level'] = !isAssessed
      ? 'not-assessed'
      : pct >= 0.5
      ? 'strong'
      : 'weak';

    const conceptQuestions = conceptMap.get(c.concept_id) ?? [];
    const bloomLevels = [
      ...new Set(
        conceptQuestions
          .map((q) => q.metadata.bloom_level)
          .filter((b): b is string => !!b)
      ),
    ]
      .map((b) => toStringBloomLevel(b))
      .filter((v) => !!v);
    const status =
      masteryMap.get(c.concept_id) ||
      (c.total_questions > 0 ? 'developing' : 'not_assessed');

    return {
      id: c.concept_id,
      name: c.title || c.name,
      emoji: c.emoji || '📘',
      total: c.total_questions,
      correct: c.total_correct_questions,
      bloomLevels,
      level,
      status: status == 'beginning' ? 'developing' : status,
    };
  });

  const assessedIds = new Set(assessed.map((c) => c.id));
  const missing: ConceptBreakdownItem[] = (
    includeMissingConcepts ? knowledgeConcept?.topological_order ?? [] : []
  )
    .filter((id) => !assessedIds.has(id))
    .reduce<ConceptBreakdownItem[]>((acc, id) => {
      const node = knowledgeConcept?.nodes?.find((n) => n.id === id);
      if (node) {
        const status = masteryMap.get(node.id) || 'not_assessed';
        acc.push({
          id: node.id,
          name: node.title,
          emoji: node.emoji || '📘',
          total: 0,
          correct: 0,
          bloomLevels: [] as string[],
          level: 'not-assessed',
          status: status == 'beginning' ? 'developing' : status,
        });
      }
      return acc;
    }, []);

  return [...assessed, ...missing];
}

function mapToStrengthWeaknessProps(items: ConceptBreakdownItem[]) {
  return items.map((val) => ({
    name: val.name,
    icon: val.emoji,
    accuracy: val.total > 0 ? formatUpiScore(val.correct / val.total) : 0,
    correct: val.correct,
    incorrect: val.total - val.correct,
    bloomLevels: val.bloomLevels as (keyof typeof BLOOM_CONFIG)[],
  }));
}

// ═══════════════════════════════════════════════════════════════════════
//  Static Styles
// ═══════════════════════════════════════════════════════════════════════

const dialogPaperSx = {
  width: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 24px 64px rgba(0, 0, 0, 0.25)',
  m: 2,
  minWidth: '100vw',
  maxWidth: '100vw',
  minHeight: '100vh',
  maxHeight: '100vh',
  borderRadius: '0px',
} as const;

const backdropSx = {
  background: 'rgba(0, 0, 0, 0.75)',
  backdropFilter: 'blur(10px)',
} as const;

const headerSxWarmup = {
  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
  padding: '12px 24px',
  textAlign: 'center',
} as const;

const headerSx = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '12px 24px',
  textAlign: 'center',
} as const;

const scrollSx = {
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
} as const;

const contentSx = {
  flex: 1,
  bgcolor: '#f8fafc',
  paddingX: { xs: '20px', md: '30px', lg: '250px' },
  paddingY: '24px',
} as const;

const footerSx = {
  padding: '20px 24px',
  paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
  bgcolor: 'white',
  borderTop: '1px solid #f1f5f9',
  display: 'flex',
  gap: '20px',
} as const;

const continueBtnSx = {
  flex: 1,
  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  color: 'white',
  padding: {
    xs: '8px 14px',
    md: '14px 20px',
  },
  borderRadius: '12px',
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 14px rgba(245,158,11,0.35)',
  '&:hover': {
    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(245,158,11,0.45)',
  },
} as const;

const endSessionSx = {
  flex: 1,
  background: '#f1f5f9',
  color: '#475569',
  padding: {
    xs: '8px 14px',
    md: '14px 20px',
  },
  borderRadius: '12px',
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    background: '#e2e8f0',
    transform: 'translateY(-2px)',
  },
} as const;

const cardSx = {
  backgroundColor: '#fff',
  borderRadius: pxToRem(20),
  padding: pxToRem(28),
  // marginBottom: pxToRem(20),
  boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  alignSelf: 'stretch',
};

// ═══════════════════════════════════════════════════════════════════════
//  Main Component
// ═══════════════════════════════════════════════════════════════════════

export const PerformancePopup: React.FC<{
  open: boolean;
  onClose: () => void;
  onEndSession?: () => void;
  results?: ReviewResult;
  mode?: 'warmup' | 'challenge' | 'end-session';
  sessionId?: string;
}> = ({ open, onClose, results, mode, onEndSession, sessionId }) => {
  const { t } = useTranslation();
  const result = results?.result;
  const totalQuestions = result?.total_questions ?? 0;
  const correctCount = result?.total_correct_questions ?? 0;
  const incorrectCount = result?.total_incorrect_questions ?? 0;
  const skippedCount = result?.total_skipped_questions ?? 0;
  const unattempedCount = result?.total_unattempted_questions ?? 0;
  const timeSpentCount = result?.total_time_taken ?? 0;
  const questions = result?.questions_data ?? [];
  const concepts = result?.concepts ?? [];
  const difficultyLevelBreakdown = result?.difficulty_level_breakdown ?? {};

  const scorePercent =
    totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0;
  const scoreMessage = t(getScoreMessage(scorePercent));
  const accuracyLabel = `${Math.round(scorePercent)}%`;
  const avgPerQuestion =
    totalQuestions > 0
      ? formatTime(Math.round(timeSpentCount / totalQuestions))
      : formatTime(0);
  const isChallenge = mode === 'challenge';
  const hasDifficulty = Object.keys(difficultyLevelBreakdown).length > 0;

  const conceptBreakdown = useMemo(
    () =>
      buildConceptBreakdown(
        concepts,
        questions,
        results?.moduleMastery,
        results?.knowledgeConcept,
        mode === 'warmup'
      ),
    [concepts, questions, results?.moduleMastery, results?.knowledgeConcept]
  );

  const strongConcepts = useMemo(
    () => conceptBreakdown.filter((c) => c.level === 'strong'),
    [conceptBreakdown]
  );
  const weakConcepts = useMemo(
    () => conceptBreakdown.filter((c) => c.level === 'weak'),
    [conceptBreakdown]
  );

  const conceptsMastered = conceptBreakdown.filter(
    (c) => c.status === 'mastered'
  ).length;
  const conceptsAttempted = conceptBreakdown.filter((c) => c.total > 0).length;

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: dialogPaperSx }}
      slotProps={{ backdrop: { sx: backdropSx } }}
    >
      <Box sx={scrollSx}>
        <Box sx={mode === 'warmup' ? headerSxWarmup : headerSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 0.75,
            }}
          >
            <Typography component="span" sx={{ fontSize: 26, lineHeight: 1 }}>
              {mode === 'warmup' ? '📊' : mode === 'challenge' ? '🏆' : '🎉'}
            </Typography>
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 800,
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {mode === 'warmup'
                ? t('PAL_PP_WARMUP_COMPLETE')
                : mode === 'challenge'
                ? t('PAL_PP_CHALLENGE_COMPLETE')
                : t('PAL_PP_SESSION_COMPLETE')}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 14,
              color: 'rgba(255,255,255,0.9)',
              fontWeight: 500,
            }}
          >
            {t('PAL_PP_HOW_YOU_DID', { count: totalQuestions })}
          </Typography>
        </Box>

        <Box sx={contentSx}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '30px',
              width: '100%',
              mb: '30px',
              alignItems: 'center',
            }}
          >
            {results?.summaryText && (
              <Box
                sx={{
                  width: '100%',
                  bgcolor: '#fff',
                  borderRadius: pxToRem(12),
                  border: '1px solid #e2e8f0',
                  borderLeft: `6px solid ${
                    isChallenge ? '#a78bfa' : '#f59e0b'
                  }`,
                  px: { xs: 2, md: 3 },
                  py: { xs: 2, md: 2.5 },
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
                }}
              >
                <Typography
                  sx={{
                    fontSize: pxToRem(14),
                    fontWeight: 500,
                    color: '#475569',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: results?.summaryText ?? '',
                  }}
                />
              </Box>
            )}
            {isChallenge ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: '30px',
                    width: '100%',
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
                    <TopbarCard
                      topbar={{
                        score: correctCount,
                        total: totalQuestions,
                        message: scoreMessage,
                        layout: 'stacked',
                        stats: {
                          correct: correctCount,
                          incorrect: incorrectCount || 0,
                          timedOut: unattempedCount,
                          skipped: skippedCount,
                          time: formatTime(timeSpentCount),
                          timeSubtitle: t('PAL_STAT_AVG_SUB', {
                            time: avgPerQuestion,
                          }),
                        },
                      }}
                    />
                  </Box>

                  {hasDifficulty ? (
                    <Box sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
                      <DifficultyBreakdownSection
                        data={Object.keys(difficultyLevelBreakdown).map(
                          (val) => {
                            return {
                              level:
                                val === 'LOW'
                                  ? 'easy'
                                  : val === 'MEDIUM'
                                  ? 'medium'
                                  : 'hard',
                              correct: difficultyLevelBreakdown[val].correct,
                              total: difficultyLevelBreakdown[val].total,
                              timeTaken:
                                difficultyLevelBreakdown[val].total_time_taken,
                            };
                          }
                        )}
                      />
                    </Box>
                  ) : null}
                </Box>
              </>
            ) : (
              <>
                <Box sx={cardSx}>
                  <TopbarCard
                    topbar={{
                      score:
                        mode === 'end-session'
                          ? formatUpiScore(
                              results?.moduleMastery?.progress
                                ?.upi_score_aggregate || 0
                            )
                          : correctCount,
                      total: mode === 'end-session' ? 100 : totalQuestions,
                      content:
                        mode === 'end-session'
                          ? t('PAL_PP_MASTERY_SCORE')
                          : undefined,
                      message:
                        mode === 'end-session' ? undefined : scoreMessage,
                      stats: {
                        correct: correctCount,
                        incorrect: incorrectCount,
                        skipped: skippedCount,
                        time: formatTime(timeSpentCount),
                        accuracy: accuracyLabel,
                        avgPerQuestion,
                      },
                    }}
                  />
                </Box>

                {hasDifficulty && (
                  <Box sx={cardSx}>
                    <DifficultyBreakdownSection
                      data={Object.keys(difficultyLevelBreakdown).map((val) => {
                        return {
                          level:
                            val === 'LOW'
                              ? 'easy'
                              : val === 'MEDIUM'
                              ? 'medium'
                              : 'hard',
                          correct: difficultyLevelBreakdown[val].correct,
                          total: difficultyLevelBreakdown[val].total,
                          timeTaken:
                            difficultyLevelBreakdown[val].total_time_taken,
                        };
                      })}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: '30px',
                    width: '100%',
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
                    <ConceptSection
                      data={{
                        noOfAttempted: conceptsAttempted,
                        noOfMastered: conceptsMastered,
                        list: conceptBreakdown.map((val) => ({
                          name: val.name,
                          icon: val.emoji,
                          correct: val.correct,
                          total: val.total,
                          status: val.status,
                        })),
                      }}
                    />
                  </Box>

                  <Box sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
                    <StrengthWeaknessSection
                      strengths={mapToStrengthWeaknessProps(strongConcepts)}
                      weaknesses={mapToStrengthWeaknessProps(weakConcepts)}
                      cardHeadingSx={{
                        fontSize: pxToRem(14),
                      }}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>

          {questions.length > 0 && (
            <QuestionReviews
              questions={questions}
              concepts={concepts}
              sessionId={sessionId}
            />
          )}
        </Box>
      </Box>

      <Box sx={footerSx}>
        {onEndSession && (
          <Button onClick={onEndSession} sx={endSessionSx}>
            {t('PAL_PP_EXIT_SESSION')}
          </Button>
        )}
        <Button onClick={onClose} sx={continueBtnSx}>
          {t('PAL_PP_CONTINUE_LEARNING')}
        </Button>
      </Box>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  Question Reviews
// ═══════════════════════════════════════════════════════════════════════

const QuestionReviews: React.FC<{
  questions: IPerformanceQuestionData[];
  concepts: IConceptSummary[];
  sessionId?: string;
}> = ({ questions, concepts, sessionId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'loading'
  );
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [geneoQuestions, setGeneoQuestions] = useState<Question[]>([]);

  const { ContentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const conceptMap = useMemo(
    () => new Map(concepts.map((c) => [c.concept_id, c])),
    [concepts]
  );

  useEffect(() => {
    setSelectedIdx(0);
    let cancelled = false;

    (async () => {
      try {
        setLoading('loading');
        const ids = questions
          .map((q) => q?.eventReqBody?.geneo_question_id)
          .filter((v): v is string => !!v);

        if (ids.length) {
          const res =
            await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchQuestions(
              {
                questionIds: ids,
              }
            );
          if (!cancelled) setGeneoQuestions(res?.data?.questions ?? []);
        } else {
          if (!cancelled) setGeneoQuestions([]);
        }
        if (!cancelled) setLoading('completed');
      } catch {
        if (!cancelled) setLoading('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [questions]);

  if (loading === 'loading') {
    return (
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: '#1e293b',
          textAlign: 'center',
          my: '10px',
        }}
      >
        {t('PAL_PP_LOADING_ANSWERS')}
      </Typography>
    );
  }

  if (loading === 'error') return null;

  const selectedQ = questions[selectedIdx];
  const geneoQues = selectedQ
    ? geneoQuestions.find(
        (g) => g.questionId === selectedQ.eventReqBody?.geneo_question_id
      )
    : undefined;
  const selectedScore = (selectedQ?.eventReqBody?.score as Score)?.value as
    | number
    | undefined;
  const reviewResultStatus = selectedQ
    ? getQuestionReviewStatus(selectedQ)
    : 'incorrect';
  const isSelectedCorrect = reviewResultStatus === 'correct';
  const conceptInfo = selectedQ?.concept_id
    ? conceptMap.get(selectedQ.concept_id)
    : undefined;
  const selectedTimeTaken = selectedQ?.eventReqBody?.time_taken_seconds;
  const selectedDifficulty = selectedQ?.metadata?.difficulty;
  const selectedConfidence = selectedQ
    ? buildOverlayCopy({
        score: selectedScore,
        streak: 0,
        timeTaken: selectedTimeTaken ?? 0,
        idealMaxTime: 120,
        isFirstQuestion: false,
      }).confidence
    : undefined;

  const userSubmittedAnswers =
    geneoQues && selectedQ?.eventReqBody?.answer
      ? getUserSubmittedDisplayAnswer(
          geneoQues,
          selectedQ.eventReqBody.answer,
          selectedScore
        )
      : [];

  const correctAnswers =
    geneoQues && selectedQ?.eventReqBody?.correct_answer
      ? getCorrectDisplayAnswer(
          geneoQues,
          selectedQ.eventReqBody.correct_answer
        )
      : [];

  const allReviewOptions = geneoQues
    ? getAllReviewOptions(
        geneoQues,
        selectedQ?.eventReqBody?.answer ?? [],
        selectedQ?.eventReqBody?.correct_answer
      )
    : [];

  const showAllOptions = allReviewOptions.length > 0;

  return (
    <>
      <Typography sx={{ fontSize: 18, mb: 1.5 }}>
        {t('PAL_PP_REVIEW_YOUR_ANSWERS')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          mb: '20px',
          p: '16px 20px',
          bgcolor: 'white',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          boxSizing: 'border-box',
        }}
      >
        {questions.map((q, idx) => {
          const isSelected = idx === selectedIdx;
          const statusStyle = QUESTION_CHIP_STYLES[getQuestionReviewStatus(q)];
          return (
            <Box
              key={idx}
              role="button"
              onClick={() => setSelectedIdx(idx)}
              sx={{
                width: 42,
                height: 42,
                borderRadius: '12px',
                border: '1.5px solid',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                ...(isSelected
                  ? {
                      borderColor: '#6366f1',
                      bgcolor: '#6366f1',
                      color: '#fff',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 10px rgba(99, 102, 241, 0.35)',
                    }
                  : {
                      ...statusStyle,
                      '&:hover': {
                        borderColor: '#cbd5e1',
                        bgcolor: '#f8fafc',
                      },
                    }),
              }}
            >
              {t('PAL_PP_QUESTION_CHIP', { n: idx + 1 })}
            </Box>
          );
        })}
      </Box>

      {selectedQ && geneoQues && (
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          }}
        >
          <QuestionAnswerReviewBox
            questionNumber={selectedIdx + 1}
            resultStatus={reviewResultStatus}
            question={geneoQues}
            conceptLabel={conceptInfo?.title || conceptInfo?.name}
            conceptEmoji={conceptInfo?.emoji || '📘'}
            size="large"
            flattenBottom
            showStats
            difficulty={selectedDifficulty}
            timeTakenSeconds={selectedTimeTaken}
            confidence={selectedConfidence}
          >
            <ReviewAnswerSolutionTabs
              resetKey={selectedQ.question_id}
              hasSolution={(selectedQ.metadata.solution || []).length > 0}
              answer={
                showAllOptions ? (
                  <AllOptionsSection options={allReviewOptions} embedded />
                ) : (
                  <>
                    <UserAnswerSection
                      answers={userSubmittedAnswers}
                      embedded
                    />
                    {!isSelectedCorrect && (
                      <CorrectAnswerSection
                        correctAnswers={correctAnswers}
                        userSubmittedAnswers={userSubmittedAnswers}
                        embedded
                      />
                    )}
                  </>
                )
              }
              solution={
                <SolutionSection solution={selectedQ.metadata.solution || []} />
              }
            />
          </QuestionAnswerReviewBox>
          <Box
            sx={{
              px: 3.5,
              py: '12px',
              borderTop: '1px dashed #e2e8f0',
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: 1.5,
            }}
          >
            {sessionId && selectedQ.question_id && (
              <ExplanationHelpfulFeedback
                sessionId={sessionId}
                questionId={selectedQ.question_id}
              />
            )}
            <Box
              sx={{
                width: '1px',
                alignSelf: 'stretch',
                bgcolor: '#e2e8f0',
                flexShrink: 0,
                display: { xs: 'none', sm: 'block' },
                minHeight: 28,
              }}
            />
            {sessionId && selectedQ.question_id && (
              <ReportIssueButton
                sessionId={sessionId}
                questionId={selectedQ.question_id}
                questionNumber={selectedIdx + 1}
                conceptName={conceptInfo?.title || conceptInfo?.name}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export const formatUpiScore = (aggregate: number) => {
  return Math.round(aggregate * 100);
};
